import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import * as ReactRouterDOM from 'react-router-dom';
import { fetchBlogData, getCachedBlogData, getFastBlogData, blogPostPath, prefetchBlogPost } from '../lib/publicBlogApi';
import DoneForYouModal from '../components/DoneForYouModal';
import {
  ArrowRight, AlertCircle
} from 'lucide-react';
import GlobalHeader from '../components/GlobalHeader';
import GlobalFooter from '../components/GlobalFooter';
import PageSEO from '../components/PageSEO';
import { getBlogImageUrl } from '../lib/utils';
import './BlogPostPage.css';
import { blogSeo, blogUrl } from '../lib/blogSeo';
import { enhanceBlogArticle } from '../lib/enhanceBlogArticle';

export default function BlogPostPage() {
  const { slug } = useParams();
  const routerLocation = typeof ReactRouterDOM.useLocation === 'function' ? ReactRouterDOM.useLocation() : null;
  const navPost = (routerLocation?.state?.initialPost && (routerLocation.state.initialPost.slug === slug || routerLocation.state.initialPost.id === slug))
    ? routerLocation.state.initialPost
    : null;

  const [post, setPost] = useState(() => {
    return getCachedBlogData(blogPostPath(slug)) || getFastBlogData(slug) || navPost || null;
  });
  const [loading, setLoading] = useState(() => {
    return !(getCachedBlogData(blogPostPath(slug)) || getFastBlogData(slug) || navPost);
  });
  const [error, setError] = useState('');
  const [notFound, setNotFound] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [copied, setCopied] = useState(false);
  const [bookingOpen, setBookingOpen] = useState(false);
  const [shareError, setShareError] = useState('');
  const copyTimer = useRef(null);
  useEffect(() => () => clearTimeout(copyTimer.current), []);
  const [activeTocId, setActiveTocId] = useState('');
  const { html: articleHtml, headings } = useMemo(() => {
    const document = new DOMParser().parseFromString(post?.content || '', 'text/html');
    enhanceBlogArticle(document, post?.slug);
    const headings = Array.from(document.querySelectorAll('h2, h3')).map((el, index) => {
      const id = `article-section-${index}`;
      el.id = id;
      return { id, text: el.textContent.trim(), level: el.tagName.toLowerCase() };
    });
    return { html: document.body.innerHTML, headings };
  }, [post?.content, post?.slug]);

  // Fetch blog post
  useEffect(() => {
    let active = true;
    const initial = getCachedBlogData(blogPostPath(slug)) || getFastBlogData(slug) || (routerLocation?.state?.initialPost?.slug === slug ? routerLocation.state.initialPost : null);
    if (!initial) {
      setLoading(true);
    }
    setError('');
    setNotFound(false);
    if (initial) {
      setPost(initial);
    }
    setActiveTocId('');
    setCopied(false);
    setBookingOpen(false);
    setShareError('');
    clearTimeout(copyTimer.current);
    window.scrollTo({ top: 0, behavior: 'instant' });

    fetchBlogData(blogPostPath(slug))
      .then(data => {
        if (!active) return;
        setPost(data);
      })
      .catch(err => {
        if (!active) return;
        if (!initial) {
          const missing = err.response?.status === 404;
          setNotFound(missing);
          setError(missing ? "The article may have been moved or unpublished." : "The article could not be loaded. Please try again.");
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => { active = false; };
  }, [slug, attempt]);

  // Scroll spy for Table of Contents active heading
  useEffect(() => {
    if (loading || headings.length === 0) return;

    const handleScroll = () => {
      let activeId = headings[0].id;
      for (let i = headings.length - 1; i >= 0; i--) {
        const el = document.getElementById(headings[i].id);
        if (el && el.getBoundingClientRect().top <= 180) {
          activeId = headings[i].id;
          break;
        }
      }
      setActiveTocId(activeId);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [headings, loading]);

  const scrollToHeading = (id) => {
    const el = document.getElementById(id);
    if (el) {
      const yOffset = -110;
      const y = el.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      setActiveTocId(id);
    }
  };

  // Share handlers
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';

  const handleShareLinkedIn = () => {
    const shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`;
    window.open(shareUrl, '_blank', 'noopener,noreferrer,width=600,height=600');
  };

  const handleShareEmail = () => {
    const subject = encodeURIComponent(post?.title || 'Interesting Article');
    const body = encodeURIComponent(`Check out this article on AI Search Advisers:\n\n${currentUrl}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleCopyLink = async () => {
    setShareError('');
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      clearTimeout(copyTimer.current);
      copyTimer.current = setTimeout(() => setCopied(false), 2500);
    } catch {
      setShareError('Unable to copy. Please copy the address from your browser.');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return Number.isNaN(date.getTime()) ? '' : date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  // Loading Screen: Minimal sleek loading state instead of jarring chunky skeleton blocks
  if (loading && !post) {
    return (
      <div className="blog-post-page blog-detail-page">
        <div className="blog-top-progress-bar" role="progressbar" aria-label="Loading article" />
        <GlobalHeader />
        <header className="blog-hero blog-hero-fast-loading" aria-busy="true">
          <div className="blog-hero-inner">
            <nav aria-label="Breadcrumb" className="blog-breadcrumbs">
              <Link to="/">Home</Link>
              <span className="blog-breadcrumbs-separator">&gt;</span>
              <Link to="/blogs">The AI Search Return Blog</Link>
            </nav>
            <div className="blog-category-eyebrow">
              <span className="blog-category-eyebrow-text">AI SEARCH ADVISER</span>
            </div>
            <h1 className="blog-hero-title blog-hero-title-loading">
              Loading article...
            </h1>
            <div className="blog-loading-pulse-ring">
              <div className="blog-loading-spinner-gold" />
            </div>
          </div>
        </header>
        <GlobalFooter />
      </div>
    );
  }

  // Not Found / Error Screen
  if (error || !post) {
    return (
      <div className="blog-post-page blog-detail-page">
        <GlobalHeader />
        <div style={{ minHeight: '75vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '140px 24px', textAlign: 'center' }}>
          <div style={{ width: '64px', height: '64px', borderRadius: '50%', background: '#FEE2E2', color: '#DC2626', display: 'grid', placeItems: 'center', marginBottom: '20px' }}>
            <AlertCircle size={32} />
          </div>
          <h1 style={{ fontFamily: 'Fraunces, serif', fontSize: '2.5rem', color: '#06203A', marginBottom: '12px' }}>
            {notFound ? 'Article Not Found' : 'Unable to load article'}
          </h1>
          <p style={{ color: '#4A5568', maxWidth: '500px', marginBottom: '28px', lineHeight: 1.6 }}>
            {error || "The article you are looking for may have been moved, updated, or unpublished."}
          </p>
          {!notFound && <button type="button" className="blog-retry-button" onClick={() => setAttempt(value => value + 1)}>Try again</button>}
          <Link
            to="/blogs"
            style={{ display: 'inline-block', background: '#FEAB3D', color: '#021426', padding: '12px 28px', fontWeight: 600, fontSize: '0.875rem', textDecoration: 'none' }}
          >
            View All Articles &rarr;
          </Link>
        </div>
        <GlobalFooter />
      </div>
    );
  }

  const categoryName = post.category_name || "General";
  const authorName = post.author_name || "AI Search Advisers";
  const publishedDateFormatted = formatDate(post.published_at || post.created_at);
  const readingTime = post.reading_time || 6;
  const postExcerpt = (post.excerpt && post.excerpt.trim()) 
    || (post.meta_description && post.meta_description.trim())
    || (post.content ? post.content.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 180) : '');

  const seo = blogSeo(post);

  return (
    <div className="blog-post-page blog-detail-page">
      <PageSEO {...seo} />

      <GlobalHeader />

      {/* ── 1. HERO SECTION (Matches Screenshot 1) ── */}
      <header className="blog-hero">
        <div className="blog-hero-inner">
          {/* Breadcrumbs */}
          <nav aria-label="Breadcrumb" className="blog-breadcrumbs">
            <Link to="/">Home</Link>
            <span className="blog-breadcrumbs-separator">&gt;</span>
            <Link to="/blogs">The AI Search Return Blog</Link>
            <span className="blog-breadcrumbs-separator">&gt;</span>
            <span className="blog-breadcrumbs-current">{categoryName}</span>
          </nav>

          {/* Category Eyebrow with decorative lines */}
          <div className="blog-category-eyebrow">
            <span className="blog-category-name">{categoryName}</span>
          </div>

          {/* Large Serif Title */}
          <h1 className="blog-hero-title">
            {post.title}
          </h1>

          {/* Excerpt / Subtitle below Title */}
          {postExcerpt && (
            <p className="blog-hero-excerpt">
              {postExcerpt}
            </p>
          )}

          {/* Meta line: DATE • AUTHOR • READ TIME */}
          <div className="blog-hero-meta">
            {publishedDateFormatted && <><span>{publishedDateFormatted}</span><span className="blog-hero-meta-dot">&bull;</span></>}
            <span>{authorName}</span>
            <span className="blog-hero-meta-dot">&bull;</span>
            <span>{readingTime} MIN READ</span>
          </div>
        </div>
      </header>

      {/* ── 2. FEATURED IMAGE BANNER (Matches Screenshot 1 & 2) ── */}
      {post.featured_image && (
        <section className="blog-featured-image-band" aria-label="Article image">
        <div className="blog-featured-image-wrapper">
          <div className="blog-featured-image-card">
            <img
              fetchPriority="high"
              decoding="async"
              src={getBlogImageUrl(post.featured_image)}
              alt={post.title}
            />
          </div>
        </div>
        </section>
      )}

      {/* ── 3. MAIN ARTICLE LAYOUT: 3 COLUMNS (Matches Screenshot 2 & 3) ── */}
      <main className="blog-content-section">
        <div className="blog-article-grid">
          
          {/* ── LEFT COLUMN: STICKY SHARE SIDEBAR ── */}
          <aside className="blog-share-sidebar" aria-label="Share article">
            <div className="blog-share-sticky-box">
              <span className="blog-share-label">SHARE</span>
              <div className="blog-share-buttons">
                <button
                  type="button"
                  onClick={handleShareLinkedIn}
                  className="blog-share-btn"
                  title="Share on LinkedIn"
                >
                  <span>LINKEDIN</span>
                </button>

                <button
                  type="button"
                  onClick={handleShareEmail}
                  className="blog-share-btn"
                  title="Share via Email"
                >
                  <span>EMAIL</span>
                </button>

                <button
                  type="button"
                  onClick={handleCopyLink}
                  className={`blog-share-btn ${copied ? 'copied' : ''}`}
                  title="Copy link to clipboard"
                >
                  <span>{copied ? 'COPIED' : 'COPY LINK'}</span>
                </button>
              </div>
            </div>
            <p role="status" className="blog-share-status">{shareError || (copied ? 'Link copied.' : '')}</p>
          </aside>

          {/* ── CENTER COLUMN: ARTICLE BODY CONTENT ── */}
          <article className="blog-main-content">
            {/* HTML Body Render */}
            <div
              className="blog-body-html"
              dangerouslySetInnerHTML={{ __html: articleHtml }}
            />

            <div className="blog-author-note">
              <span className="blog-author-initial" aria-hidden="true">{authorName.charAt(0)}</span>
              <div><p className="blog-section-eyebrow">WRITTEN BY</p><strong>{authorName}</strong>{post.author?.bio && <p>{post.author.bio}</p>}</div>
            </div>

            <section className="blog-consultation-cta" aria-labelledby="blog-consultation-title">
              <p className="blog-section-eyebrow">YOUR NEXT STEP</p>
              <h2 id="blog-consultation-title">Turn AI search insights into a plan for your business.</h2>
              <p>Talk with us about your visibility, your goals, and where to focus next. Book a 30-minute consultation at a time that works for you.</p>
              <div className="blog-consultation-actions">
                <button type="button" onClick={() => setBookingOpen(true)}>Book a call <ArrowRight size={16} aria-hidden="true" /></button>
                <Link to="/scan">Explore your AI visibility <ArrowRight size={16} aria-hidden="true" /></Link>
              </div>
            </section>

            {/* ── ARTICLE FOOTER: TAGS & DISCLAIMER (Screenshot 3) ── */}
            <footer className="blog-article-footer">
                <nav className="blog-tags-container" aria-label="Article categories and tags">
                  <Link to={post.category?.slug ? `/blogs?category=${encodeURIComponent(post.category.slug)}` : '/blogs'} className="blog-sand-tag">{categoryName}</Link>
                  {(post.tags || []).filter(tag => tag.toLowerCase() !== categoryName.toLowerCase()).map((tag, idx) => (
                    <Link
                      key={idx}
                      to={`/blogs?tag=${encodeURIComponent(tag)}`}
                      className="blog-sand-tag"
                    >
                      {tag}
                    </Link>
                  ))}
                </nav>

              {/* Disclaimer matching Screenshot 3 */}
              <div className="blog-disclaimer">
                <strong>Disclaimer:</strong> This article is for informational purposes only. AI search technologies and algorithms are constantly evolving, and AI Search Advisers does not guarantee specific rankings, traffic, leads, or business results. All third-party trademarks belong to their respective owners, and references do not imply affiliation or endorsement.
              </div>
            </footer>
          </article>

          {/* ── RIGHT COLUMN: STICKY SIDEBAR (Screenshot 2 & 3) ── */}
          <aside className="blog-right-sidebar">
            <div className="blog-right-sticky-box">
              {/* "IN THIS ARTICLE" Table of Contents (Screenshot 2) */}
              {headings.length > 0 && (
                <div className="blog-toc-card">
                  <div className="blog-toc-header">
                    IN THIS ARTICLE
                  </div>
                  <ul className="blog-toc-list">
                    {headings.map((h) => (
                      <li key={h.id} className="blog-toc-item">
                        <button
                          type="button"
                          onClick={() => scrollToHeading(h.id)}
                          aria-current={activeTocId === h.id ? 'location' : undefined}
                          data-level={h.level}
                          className={`blog-toc-link text-left w-full ${activeTocId === h.id ? 'active' : ''}`}
                        >
                          {h.text}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* CTA Sidebar Card (Screenshot 3) */}
              <div className="blog-cta-card">
                <h4 className="blog-cta-card-title">
                  How visible is your business in AI search?
                </h4>
                <p className="blog-cta-card-text">
                  Start with your search discovery needs, then determine how AI engines perceive and cite your business.
                </p>
                <Link to="/scan" className="blog-cta-card-btn">
                  <span>EXPLORE AUDIT</span>
                  <ArrowRight size={14} />
                </Link>
              </div>
            </div>
          </aside>

        </div>
      </main>

      {/* ── 4. RELATED INSIGHTS SECTION (Matches Screenshot 4) ── */}
      {post.related_posts && post.related_posts.length > 0 && (
        <section className="blog-related-section">
          <div className="blog-related-inner">
            <div className="blog-related-top">
              <h2 className="blog-related-heading">
                Related Insights
              </h2>
              <Link to="/blogs" className="blog-related-all-link">
                <span>VIEW ALL ARTICLES</span>
                <ArrowRight size={14} />
              </Link>
            </div>

            <div className="blog-related-grid">
              {post.related_posts.map((item) => (
                <Link
                  key={item.id}
                  to={new URL(blogUrl(item)).pathname}
                  state={{ initialPost: item }}
                  onMouseEnter={() => prefetchBlogPost(item.slug)}
                  onFocus={() => prefetchBlogPost(item.slug)}
                  className="blog-related-card"
                >
                  {item.featured_image ? (
                    <img
                      loading="lazy"
                      src={getBlogImageUrl(item.featured_image)}
                      alt={item.title}
                      className="blog-related-card-image"
                    />
                  ) : (
                    <div className="blog-related-card-image blog-card-placeholder">
                      AI Search Advisers
                    </div>
                  )}

                  <div className="blog-related-card-body">
                    <span className="blog-related-card-category">
                      {item.category_name || "Insight"}
                    </span>
                    <h3 className="blog-related-card-title">
                      {item.title}
                    </h3>
                    <p className="blog-related-card-excerpt">
                      {item.excerpt || "Read our strategic perspective on generative search, brand authority, and digital visibility."}
                    </p>

                    <div className="blog-related-card-footer">
                      <span>{item.reading_time || 5} MIN READ</span>
                      <span className="blog-related-card-read">
                        <span>READ</span>
                        <ArrowRight size={12} />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <GlobalFooter />
      <DoneForYouModal isOpen={bookingOpen} onClose={() => setBookingOpen(false)} />
    </div>
  );
}
